
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Pricing() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const handleSelectPackage = (packageType: string, price: number) => {
    sessionStorage.setItem('selectedPackage', JSON.stringify({ packageType, price, billingCycle }));
    
    if (packageType === 'basic') {
      navigate('/add-listing');
    } else {
      navigate('/add-listing');
    }
  };

  const packages = [
    {
      id: 'basic',
      name: 'Basic Listing',
      price: { monthly: 0, yearly: 0 },
      popular: false,
      description: 'Best for businesses looking to establish an initial presence on The Revu List.',
      features: [
        'Business Name & Address',
        'Basic Category & Industry Tag',
        'Users Can Leave Reviews',
        'Appear in Search Results (Standard Ranking)',
      ],
      buttonText: 'Register Now!',
      buttonStyle: 'border-2 border-teal-600 text-teal-600 hover:bg-teal-50',
      icon: 'ri-building-line',
      color: 'gray',
    },
    {
      id: 'essential',
      name: 'Essential Listing',
      price: { monthly: 29, yearly: 348 },
      popular: false,
      description: 'Great for businesses looking to enhance visibility and credibility while making it easier for customers to connect.',
      features: [
        'Business Name, Address, and Contact Information',
        'Detailed Business Description',
        'Website & Social Media Links',
        'Logo Display',
        'Search Ranking Boost',
        'Verified Badge',
      ],
      buttonText: 'Buy Now!',
      buttonStyle: 'bg-teal-600 text-white hover:bg-teal-700',
      icon: 'ri-star-line',
      color: 'teal',
    },
    {
      id: 'enhanced',
      name: 'Enhanced Listing',
      price: { monthly: 49, yearly: 588 },
      popular: true,
      description: 'Best for businesses that want to drive engagement and gain higher placement in searches!',
      features: [
        'Everything in Essential Listing, PLUS:',
        'Ability to Upload Photos & Videos',
        'Unlimited Service Categories & Keywords',
        'Priority Search Placement (Above Basic & Essential Listings)',
        'Featured Review Highlight',
      ],
      buttonText: 'Buy Now!',
      buttonStyle: 'bg-gradient-to-r from-teal-600 to-blue-600 text-white hover:from-teal-700 hover:to-blue-700',
      icon: 'ri-vip-crown-line',
      color: 'blue',
    },
  ];

  const claimPackage = {
    id: 'claim',
    name: 'Claim Your Listing',
    subtitle: 'Own Your Brand!',
    price: { monthly: 29, yearly: 299 },
    description: 'Ideal for businesses that want to take control of their online presence and build credibility.',
    features: [
      'Gain Access to Your Business Profile',
      'Update Business Information & Contacts',
      'Respond to Reviews & Manage Reputation',
      'Add a Logo & Basic Branding',
    ],
    buttonText: 'Buy Now!',
    icon: 'ri-shield-check-line',
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-grow py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">Choose Your Perfect Plan</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Select the listing package that best fits your business needs and start connecting with more customers today.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-white rounded-full p-1 shadow-md">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                  billingCycle === 'monthly'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                  billingCycle === 'yearly'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  Save up to 17%
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                  pkg.popular ? 'ring-4 ring-teal-500' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-teal-600 to-blue-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}

                <div className="p-8">
                  {/* Icon */}
                  <div className={`w-16 h-16 bg-${pkg.color}-100 rounded-2xl flex items-center justify-center mb-6`}>
                    <i className={`${pkg.icon} text-3xl text-${pkg.color}-600`}></i>
                  </div>

                  {/* Package Name */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>

                  {/* Price */}
                  <div className="mb-6">
                    {pkg.price.monthly === 0 ? (
                      <div className="text-4xl font-bold text-gray-900">FREE</div>
                    ) : (
                      <>
                        <div className="flex items-baseline">
                          <span className="text-4xl font-bold text-gray-900">
                            ${billingCycle === 'monthly' ? pkg.price.monthly : pkg.price.yearly}
                          </span>
                          <span className="text-gray-600 ml-2">
                            /{billingCycle === 'monthly' ? 'month' : 'year'}
                          </span>
                        </div>
                        {billingCycle === 'yearly' && (
                          <div className="text-sm text-green-600 font-medium mt-1">
                            Save ${(pkg.price.monthly * 12 - pkg.price.yearly).toFixed(0)}/year
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 mb-6 min-h-[60px]">
                    💡 {pkg.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <i className="ri-check-line text-teal-600 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Button */}
                  <button
                    onClick={() => handleSelectPackage(pkg.id, billingCycle === 'monthly' ? pkg.price.monthly : pkg.price.yearly)}
                    className={`w-full px-6 py-3 rounded-lg font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${pkg.buttonStyle}`}
                  >
                    {pkg.buttonText}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Claim Your Listing Section */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="p-8 lg:p-12">
                <div className="inline-flex items-center bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <i className="ri-star-fill mr-1"></i>
                  Already Listed?
                </div>
                
                <h2 className="text-4xl font-bold text-gray-900 mb-2">{claimPackage.name}</h2>
                <p className="text-2xl text-purple-600 font-semibold mb-6">{claimPackage.subtitle}</p>
                
                <p className="text-lg text-gray-600 mb-6">
                  Already listed on The Revu List? Take control and enhance your profile!
                </p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900">
                      ${billingCycle === 'monthly' ? claimPackage.price.monthly : claimPackage.price.yearly}
                    </span>
                    <span className="text-gray-600 ml-2">
                      /{billingCycle === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <div className="text-sm text-green-600 font-medium mt-1">
                      Save ${(claimPackage.price.monthly * 12 - claimPackage.price.yearly).toFixed(0)}/year
                    </div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {claimPackage.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-check-line text-purple-600 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-gray-600 mb-6">
                  💡 {claimPackage.description}
                </p>

                <button
                  onClick={() => handleSelectPackage(claimPackage.id, billingCycle === 'monthly' ? claimPackage.price.monthly : claimPackage.price.yearly)}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
                >
                  {claimPackage.buttonText}
                </button>
              </div>

              <div className="relative h-full min-h-[400px] lg:min-h-[600px]">
                <img
                  alt="Claim your listing"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20owner%20managing%20their%20online%20business%20profile%20on%20laptop%20in%20modern%20office%2C%20confident%20entrepreneur%20updating%20business%20listing%20and%20responding%20to%20customer%20reviews%2C%20bright%20professional%20workspace%20with%20plants%20and%20natural%20lighting&width=600&height=800&seq=claim-listing-hero&orientation=portrait"
                />
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {[
                {
                  question: 'Can I upgrade my plan later?',
                  answer: 'Yes! You can upgrade your plan at any time. The price difference will be prorated for the remainder of your billing cycle.',
                },
                {
                  question: 'What payment methods do you accept?',
                  answer: 'We accept all major credit cards, PayPal, and Apple Pay for your convenience.',
                },
                {
                  question: 'Can I cancel anytime?',
                  answer: 'Absolutely! You can cancel your subscription at any time. Your listing will remain active until the end of your current billing period.',
                },
                {
                  question: 'How long does it take to get listed?',
                  answer: 'Free listings are instant! Paid listings are reviewed and published within 24 hours to ensure quality.',
                },
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start">
                    <i className="ri-question-line text-teal-600 text-xl mr-2 mt-0.5"></i>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 ml-8">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Trust Section */}
          <div className="mt-16 text-center">
            <div className="flex justify-center items-center gap-12 flex-wrap">
              <div className="flex items-center text-gray-600">
                <i className="ri-shield-check-line text-3xl text-teal-600 mr-3"></i>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">Secure Payment</div>
                  <div className="text-sm">SSL Encrypted</div>
                </div>
              </div>
              <div className="flex items-center text-gray-600">
                <i className="ri-customer-service-line text-3xl text-teal-600 mr-3"></i>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">24/7 Support</div>
                  <div className="text-sm">We're here to help</div>
                </div>
              </div>
              <div className="flex items-center text-gray-600">
                <i className="ri-refresh-line text-3xl text-teal-600 mr-3"></i>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">Cancel Anytime</div>
                  <div className="text-sm">No long-term contracts</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
